import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import 'react-csv-importer/dist/index.css';

import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    LinearProgress,
    Modal,
    Stack,
    Typography,
    Alert,
    IconButton,
    Paper,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon, Upload as UploadIcon } from '@mui/icons-material';

import { Importer, ImporterField } from 'react-csv-importer';
import MainCard from 'ui-component/cards/MainCard';
import SendEmailToStand from '../../Components/SendEmailToStand';
import CompanyImportHistory from './CompanyImportHistory';
import { GET_ME } from 'graphql/queries';

const IMPORT_COMPANIES = gql`
    mutation ImportCompanies($eventId: String!, $role: String!, $jsonData: String!) {
        importCompanies(eventId: $eventId, role: $role, jsonData: $jsonData) {
            total
            inserted
            duplicates
            failed
            failedRows
        }
    }
`;

const GET_IMPORT_HISTORY = gql`
    query GetCompanyImportHistory($eventId: String!, $role: String!) {
        companyImportHistory(eventId: $eventId, role: $role)
    }
`;

const GET_ALL_EMAIL_TEMPLATES_FOR_STANDS = gql`
    query GetAllEmailTemplatesForStands($eventId: String!) {
        getAllEmailTemplatesForStands(eventId: $eventId) {
            id
            role
            templateType
            subject
            body
            isHtml
        }
    }
`;

// User-friendly error messages
const getUserFriendlyError = error => {
    const errorMap = {
        'Missing required field: Email': 'Email address is required',
        'Missing required field: Company': 'Company name is required',
        'Missing required field: allowedRegistrations': 'Allowed registrations is required',
        'Invalid email format': 'Please provide a valid email address',
        'Duplicate email': 'This email already exists. Please use a unique email',
        'Bad characters detected': 'Invalid characters found in data. Please check and remove special characters',
        'Unknown error occurred': 'An unexpected error occurred. Please try again or contact support',
    };
    return errorMap[error] || error;
};

export default function SponsorExhibitorImporter({ importRole }) {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { data, loading, error } = useQuery(GET_ME);

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography>{error?.message}</Typography>;
    }

    if (!data?.me?.permissions?.includes('admin') && !data?.me?.permissions?.includes('superadmin')) {
        return <div>Access denied</div>;
    }

    return <MyImporter me={data} role={importRole} />;
}

function MyImporter({ me, role }) {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [uploadResult, setUploadResult] = useState(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const isSponsors = String(role).toLowerCase() === 'sponsors';
    const isExhibitors = String(role).toLowerCase() === 'exhibitors';

    // Required fields based on role
    const REQUIRED_FIELDS = isSponsors
        ? ['Email', 'Company', 'allowedRegistrations', 'allowed_no_of_staff']
        : ['Email', 'Company', 'allowedRegistrations'];
    const OPTIONAL_FIELDS = ['StallNo', 'First Name', 'Last Name', 'Designation'];

    const [doImport, { loading: uploading }] = useMutation(IMPORT_COMPANIES, {
        onError: e => {
            console.error('Import error:', e);
            setUploadResult({
                success: false,
                message: e.message || 'Failed to import data',
                detailedReport: {
                    summary: {
                        totalRows: 0,
                        successfulInsertions: 0,
                        failedInsertions: 0,
                    },
                    failedRows: [],
                    successfulRows: [],
                },
            });
            setShowResultModal(true);
        },
        onCompleted: e => {
            try {
                const result = e?.importCompanies;
                const failedRows = result.failedRows ? JSON.parse(result.failedRows) : [];
                const successfulRows = [];

                // Create successful rows from inserted count
                for (let i = 0; i < result.inserted; i++) {
                    successfulRows.push({
                        rowNumber: i + 1,
                        status: 'success',
                    });
                }

                setUploadResult({
                    success: result.inserted > 0,
                    message: `Imported ${result.inserted} ${role.toLowerCase()}, ${result.duplicates} duplicates, ${
                        result.failed
                    } failed`,
                    detailedReport: {
                        summary: {
                            totalRows: result.total,
                            successfulInsertions: result.inserted,
                            failedInsertions: result.failed,
                            duplicates: result.duplicates,
                        },
                        failedRows: failedRows,
                        successfulRows: successfulRows,
                    },
                });
                setShowResultModal(true);
            } catch (error) {
                console.error('Error parsing import result:', error);
                setUploadResult({
                    success: false,
                    message: 'Error processing import result',
                    detailedReport: {
                        summary: {
                            totalRows: 0,
                            successfulInsertions: 0,
                            failedInsertions: 0,
                        },
                        failedRows: [],
                        successfulRows: [],
                    },
                });
                setShowResultModal(true);
            }
        },
    });

    const generateCSVFile = (fields, data = []) => {
        let header = fields;
        if (data.length > 0 && typeof data[0] === 'object') {
            header = Object.keys(data[0]);
        }
        const csvRows = [];
        csvRows.push(header.join(','));
        if (data.length > 0) {
            data.forEach(row => {
                const values = header.map(field => {
                    return JSON.stringify(row[field] || '');
                });
                csvRows.push(values.join(','));
            });
        }
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${eventId}-${role}-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const generateSampleData = () => {
        generateCSVFile([...REQUIRED_FIELDS, ...OPTIONAL_FIELDS]);
    };

    const downloadFailedData = () => {
        if (!uploadResult?.detailedReport?.failedRows || uploadResult.detailedReport.failedRows.length === 0) return;
        generateCSVFile(['rowNumber', 'Email', 'Company', 'error', 'reason'], uploadResult.detailedReport.failedRows);
    };

    const downloadSuccessfulData = () => {
        if (!uploadResult?.detailedReport?.successfulRows || uploadResult.detailedReport.successfulRows.length === 0)
            return;
        generateCSVFile(['rowNumber', 'status'], uploadResult.detailedReport.successfulRows);
    };

    const handleUpload = async rows => {
        if (!rows || !Array.isArray(rows) || rows.length === 0) {
            return;
        }
        await doImport({
            variables: {
                eventId,
                role,
                jsonData: JSON.stringify(rows),
            },
        });
    };

    const successfulCount = uploadResult?.detailedReport?.summary?.successfulInsertions || 0;
    const failedCount = uploadResult?.detailedReport?.summary?.failedInsertions || 0;
    const duplicatesCount = uploadResult?.detailedReport?.summary?.duplicates || 0;

    return (
        <Box>
            <MainCard
                title={`Import ${role}`}
                secondary={
                    <Button variant='outlined' startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                        Back
                    </Button>
                }
            >
                {/* Helper Information Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        How to Import {role}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📋 Step 1: Prepare Your Data
                                    </Typography>
                                    <Typography variant='body2' paragraph>
                                        Ensure your CSV file includes all required fields: {REQUIRED_FIELDS.join(', ')}.
                                        Optional fields include: {OPTIONAL_FIELDS.join(', ')}.
                                    </Typography>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📤 Step 2: Upload File
                                    </Typography>
                                    <Typography variant='body2' paragraph>
                                        Use the upload area below to import your CSV or Excel file. The system will
                                        automatically validate and process your data.
                                    </Typography>
                                    <Typography variant='subtitle1' gutterBottom>
                                        ✨ Step 3: Review Results
                                    </Typography>
                                    <Typography variant='body2'>
                                        After import, review the results and download any failed data for correction.
                                        You can also send confirmation emails to successfully imported{' '}
                                        {role.toLowerCase()}.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant='subtitle1' gutterBottom>
                                        🎯 Required Fields
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {REQUIRED_FIELDS.map(field => (
                                            <Chip
                                                key={field}
                                                label={field}
                                                color='error'
                                                size='small'
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📝 Optional Fields
                                    </Typography>
                                    <Box>
                                        {OPTIONAL_FIELDS.map(field => (
                                            <Chip
                                                key={field}
                                                label={field}
                                                color='default'
                                                size='small'
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Sample Data Download */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        Download Sample Data
                    </Typography>
                    <Button variant='contained' onClick={generateSampleData}>
                        Download Sample CSV
                    </Button>
                    <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                        Use this sample file as a template for your data upload.
                    </Typography>
                </Box>

                {/* Upload Area */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        Upload {role} Data
                    </Typography>
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <Importer dataHandler={handleUpload} defaultNoHeader={false} restartable={true}>
                            {/* Required/common fields */}
                            <ImporterField name='Email' label='Email' optional={false} />
                            <ImporterField name='Company' label='Company' optional={false} />

                            {/* Optional common fields */}
                            <ImporterField name='StallNo' label='Stall No' optional />
                            <ImporterField name='First Name' label='First Name' optional />
                            <ImporterField name='Last Name' label='Last Name' optional />
                            <ImporterField name='Designation' label='Designation' optional />

                            {/* Role-specific */}
                            {isSponsors && (
                                <>
                                    <ImporterField
                                        name='allowedRegistrations'
                                        label='Allowed Registrations (VIP)'
                                        optional={false}
                                    />
                                    <ImporterField
                                        name='allowed_no_of_staff'
                                        label='Allowed Registrations (Delegate)'
                                        optional={false}
                                    />
                                </>
                            )}
                            {isExhibitors && (
                                <ImporterField
                                    name='allowedRegistrations'
                                    label='Allowed Registrations (Staff)'
                                    optional={false}
                                />
                            )}
                        </Importer>
                    </Box>
                </Box>

                {/* Upload Status */}
                {uploading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Importing data...</Typography>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Button
                        variant='outlined'
                        onClick={() => setDrawerOpen(true)}
                        disabled={!me?.me?.permissions?.includes('importhistory')}
                    >
                        See Import History
                    </Button>
                </Box>
            </MainCard>

            {/* Upload Result Modal */}
            <Modal open={showResultModal} onClose={() => setShowResultModal(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 800,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        maxHeight: '90vh',
                        overflow: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant='h5'>Import Results</Typography>
                        <IconButton onClick={() => setShowResultModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {uploadResult && (
                        <>
                            <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                                <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                                    {uploadResult.message}
                                </Typography>
                            </Alert>

                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'info.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {uploadResult.detailedReport?.summary?.totalRows || 0}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Total Rows
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'success.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {successfulCount}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Successful
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'warning.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {duplicatesCount}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Duplicates
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'error.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {failedCount}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Failed
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Download Buttons */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Download Data
                                </Typography>
                                <Stack direction='row' spacing={2} flexWrap='wrap' gap={2}>
                                    {successfulCount > 0 && (
                                        <Button variant='outlined' color='success' onClick={downloadSuccessfulData}>
                                            Download Successful ({successfulCount})
                                        </Button>
                                    )}
                                    {failedCount > 0 && (
                                        <Button variant='outlined' color='error' onClick={downloadFailedData}>
                                            Download Failed ({failedCount})
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            {/* Failed Data Details */}
                            {failedCount > 0 && uploadResult.detailedReport?.failedRows && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant='subtitle1' gutterBottom>
                                        Failed Rows Details
                                    </Typography>
                                    <Box
                                        sx={{
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }}
                                    >
                                        {uploadResult.detailedReport.failedRows.slice(0, 10).map((row, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    p: 2,
                                                    borderBottom: '1px solid #eee',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Typography variant='body2' color='error' gutterBottom>
                                                    <strong>Row {row.rowNumber || index + 1}:</strong>{' '}
                                                    {getUserFriendlyError(row.reason || row.error)}
                                                </Typography>
                                                {row.Email && (
                                                    <Typography variant='caption' color='textSecondary'>
                                                        Email: {row.Email} | Company: {row.Company || 'N/A'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                        {uploadResult.detailedReport.failedRows.length > 10 && (
                                            <Typography
                                                variant='body2'
                                                color='textSecondary'
                                                sx={{ p: 2, textAlign: 'center' }}
                                            >
                                                ... and {uploadResult.detailedReport.failedRows.length - 10} more failed
                                                rows
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Email Sending Section */}
                            {successfulCount > 0 && (
                                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant='h6' gutterBottom>
                                        Send Confirmation Emails?
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                        You can send confirmation emails to {successfulCount} successfully imported
                                        {role.toLowerCase()}. You'll be able to select a template and customize the
                                        email before sending.
                                    </Typography>
                                    <Stack direction='row' spacing={2}>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            onClick={() => {
                                                // For now, we'll need to fetch the imported companies to send emails
                                                // This would require additional backend support
                                                setShowEmailModal(true);
                                            }}
                                        >
                                            Yes, Send Emails ({successfulCount})
                                        </Button>
                                        <Button variant='outlined' onClick={() => setShowResultModal(false)}>
                                            No, Skip
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Modal>

            {/* History Drawer */}
            <Drawer
                anchor='right'
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: '60%', maxWidth: 800 } }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                        <Typography variant='h6'>Import History</Typography>
                        <IconButton onClick={() => setDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <CompanyImportHistory eventId={eventId} role={role} />
                </Box>
            </Drawer>
        </Box>
    );
}
